import React from 'react';
import moment from 'moment';
import { Chip, Grid, Typography } from '@mui/material';
import FormLabelWithIcon from './FormLabelWithIcon';
import { LABEL_GAP, SECTION_GAP } from './inputStyles';
import { DatePicker } from '../../../ClickableField/ClickableField';
import { selectedChipSx } from '../chipStyles';

/**
 * ISO on screen as well as on the wire, matching ClientV2's 生日 field, which
 * settled on the same notation for the same reason: the value is stored,
 * compared and sent as `YYYY-MM-DD`, so showing it in any other shape asks the
 * adviser to translate between two notations for one date.
 *
 * This is also why these are no longer `<input type="date">`. A native date
 * input renders in the BROWSER's locale — `mm/dd/yyyy` on an en-US machine, no
 * matter what the app is set to — and prints that pattern into the empty field
 * as a hint that cannot be turned off. ClickableField's picker shows the
 * formatted value and nothing at all when there is none.
 */
const ISO_FORMAT = 'YYYY-MM-DD';

/** ISO string in, moment (or null) out — the shape the picker wants. */
const toMoment = isoDate => {
  if (!isoDate) return null;
  const parsed = moment(isoDate, ISO_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * How long a range the adviser is asking about, offered as a length rather than
 * a second date.
 *
 * Nobody thinks "活動由 9 月 6 日到 10 月 6 日" — they think "下個月有咩活動".
 * Turning that into an end date is arithmetic the adviser was doing in their
 * head, on a native date input, on a phone. These say the thing they actually
 * mean and compute the date.
 *
 * The base is the start date, or today when none has been picked yet — in which
 * case the start is filled in too, because a length with no beginning is not a
 * range.
 */
const PRESETS = [
  { id: 'date-range-preset-1w', add: { days: 7 } },
  { id: 'date-range-preset-2w', add: { days: 14 } },
  { id: 'date-range-preset-1m', add: { months: 1 } },
  { id: 'date-range-preset-2m', add: { months: 2 } }
];

const toISODate = date => {
  // Local, not `toISOString()`: that converts to UTC first, which in HK (UTC+8)
  // moves every date before 08:00 back a day.
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * `setMonth` rolls over rather than clamping — 1 月 31 日 + 1 個月 lands on
 * 3 月 3 日 — so the day is clamped to the target month's length first, which
 * is what 「一個月後」 means to a person.
 */
const addTo = (isoDate, { days = 0, months = 0 }) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const base = new Date(year, month - 1, day);

  if (months) {
    const targetMonth = base.getMonth() + months;
    const lastDayOfTarget = new Date(base.getFullYear(), targetMonth + 1, 0).getDate();
    base.setDate(Math.min(base.getDate(), lastDayOfTarget));
    base.setMonth(targetMonth);
  }
  if (days) {
    base.setDate(base.getDate() + days);
  }

  return toISODate(base);
};

export const DateRange = ({
  intl,
  options,
  onValueChange,
  // Sets both ends at once. The presets need it: a preset tapped with no start
  // date fills in BOTH, and two `onValueChange` calls in a row would be a
  // stale-closure trap for any caller whose setter spreads previous state — as
  // this component's one caller did. Presets are only offered when it is given,
  // so a caller cannot get half of the behaviour by forgetting it.
  onRangeChange,
  classes = {},
  required = false,
  label = 'date-range',
  startField = 'startDate',
  endField = 'endDate',
  startLabel = 'start-date',
  endLabel = 'end-date',
  minDate = new Date().toISOString().split('T')[0]
}) => {
  const startDateValue = options?.[startField] || '';
  const endDateValue = options?.[endField] || '';

  const applyPreset = preset => {
    const start = startDateValue || toISODate(new Date());
    onRangeChange(start, addTo(start, preset.add));
  };

  return (
    <Grid className={classes?.sectionContainer || ''}>
      <FormLabelWithIcon
        iconName="Calendar"
        labelText={intl.formatMessage({ id: label })}
        type="orange"
        required={required}
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            format={ISO_FORMAT}
            label={intl.formatMessage({ id: startLabel })}
            value={toMoment(startDateValue)}
            onChange={picked =>
              onValueChange(startField, picked?.isValid() ? picked.format(ISO_FORMAT) : '', startDateValue)
            }
            minDate={toMoment(minDate) || undefined}
            maxDate={toMoment(endDateValue) || undefined}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            format={ISO_FORMAT}
            label={intl.formatMessage({ id: endLabel })}
            value={toMoment(endDateValue)}
            onChange={picked =>
              onValueChange(endField, picked?.isValid() ? picked.format(ISO_FORMAT) : '', endDateValue)
            }
            minDate={toMoment(startDateValue) || toMoment(minDate) || undefined}
          />
        </Grid>
      </Grid>

      {onRangeChange && (
        <Grid style={{ marginTop: SECTION_GAP }}>
          <Typography variant="caption" color="textSecondary">
            {intl.formatMessage({ id: 'date-range-preset-label' })}
          </Typography>
          <Grid container spacing={0} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: LABEL_GAP }}>
            {PRESETS.map(preset => {
              // Lit when the two fields already say what this chip would set, so
              // a range picked by chip still reads back as that length.
              const selected = !!startDateValue && !!endDateValue && endDateValue === addTo(startDateValue, preset.add);

              return (
                <Chip
                  key={preset.id}
                  label={intl.formatMessage({ id: preset.id })}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={() => applyPreset(preset)}
                  sx={selected ? selectedChipSx : undefined}
                />
              );
            })}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
